import axios from 'axios';
import store from './slices';
import { refreshToken, requirePinCode, logout, stopImpersonation } from './slices/auth.slice';
import { requireVerification } from './slices/two-factor.slice';
import { isImpersonating, isReverting } from './services/impersonation';
import { v4 as uuidv4 } from 'uuid';
import { logServerError } from './services/errorLogger';

const rolesWithoutPin = ['root', 'bac-user', 'bac-admin'];

const getDeviceId = () => {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
        deviceId = uuidv4();
        localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
};

export const getDeviceName = () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    
    let deviceName = 'Unknown Device';
    
    if (userAgent.includes('Chrome')) deviceName = 'Chrome Browser';
    else if (userAgent.includes('Firefox')) deviceName = 'Firefox Browser';
    else if (userAgent.includes('Safari')) deviceName = 'Safari Browser';
    else if (userAgent.includes('Edge')) deviceName = 'Edge Browser';
    
    if (platform.includes('Win')) deviceName += ' (Windows)';
    else if (platform.includes('Mac')) deviceName += ' (macOS)';
    else if (platform.includes('Linux')) deviceName += ' (Linux)';
    
    return deviceName;
};

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use((config) => {
    const accessToken = localStorage.getItem(config.refreshToken ? 'refreshToken' : 'token');
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    
    config.headers['x-device-id'] = getDeviceId();
    config.headers['x-device-name'] = getDeviceName();
    
    const twoFactorVerified = localStorage.getItem('2fa-verified');
    const twoFactorTimestamp = localStorage.getItem('2fa-timestamp');
    
    if (twoFactorVerified && twoFactorTimestamp) {
        config.headers['x-2fa-verified'] = twoFactorVerified;
        config.headers['x-2fa-timestamp'] = twoFactorTimestamp;
    }
    
    return config;
}, (error) => {
    return Promise.reject(error);
});

api.interceptors.response.use(response => response, async (error) => {
    const originalRequest = error.config;

    // Log server errors (skip retry attempts and expected auth errors)
    const requestUrl = originalRequest?.url || '';
    const skipLogging = originalRequest?._retry ||
        requestUrl.includes('/auth/login') ||   // failed logins are user errors, not server errors
        requestUrl.includes('/error-logs');     // never log the logger itself (avoids cascades)

    if (!skipLogging) {
        if (error.response?.status >= 400) {
            // 401s are the normal refresh/PIN handshake below, not incidents.
            if (error.response.status !== 401) {
                logServerError(error);
            }
        } else if (!error.response && error.code !== 'ERR_CANCELED') {
            // No response at all — network drop, timeout, blocked preflight. The
            // server never saw the request, so this is the only place it can be
            // recorded; it used to be dropped entirely by the status >= 400 test.
            logServerError(error);
        }
    }

    // A 401 from the fire-and-forget error logger must never trigger logout/redirect
    if (error.response?.status === 401 && originalRequest.url?.includes('/error-logs')) {
        return Promise.reject(error);
    }

    if (error.response?.status === 403 && error.response?.data?.requiresTwoFactor) {
        const action = originalRequest.url?.includes('password') ? 'changing your password' :
                      originalRequest.url?.includes('email') ? 'changing your email address' :
                      originalRequest.url?.includes('delete') ? 'deleting your account' :
                      originalRequest.url?.includes('transactions') ? 'processing this financial transaction' :
                      originalRequest.url?.includes('credit') ? 'processing this financial transaction' :
                      originalRequest.url?.includes('2fa') ? 'modifying security settings' :
                      'completing this action';
        
        store.dispatch(requireVerification(action));
        return Promise.reject(error);
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
        // Skip token refresh logic for login endpoint - let it fail naturally
        if (originalRequest.url?.includes('/auth/login')) {
            return Promise.reject(error);
        }

        // /auth/refreshToken's own 401 is handled in the slice (forceLogout flow)
        if (originalRequest.url?.includes('/auth/refreshToken')) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        // An impersonated session can't be recovered: refreshing it needs the org user's
        // PIN, which the admin doesn't have, and every branch below would spend the
        // admin's own session trying. End the impersonation instead — the admin's
        // credentials are still stashed, and this is the last moment they can be used.
        // `isReverting` latches the branch: requests that 401'd together must not fall
        // through and get retried under the admin token the revert just restored.
        if (isImpersonating() || isReverting()) {
            const result = await store.dispatch(stopImpersonation({ reason: 'expired' }));
            window.location.assign(result.payload?.exitPath || '/');
            return Promise.reject(error);
        }

        const role = localStorage.getItem('role');
        const hasPinCode = localStorage.getItem('hasPinCode') === 'true';

        if (rolesWithoutPin.includes(role)) {
            try {
                await store.dispatch(refreshToken()).unwrap();
                const newAccessToken = localStorage.getItem('token');
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return api(originalRequest);
            } catch (refreshError) {
                store.dispatch(logout());
                window.location.href = '/login';
                return Promise.reject(error);
            }
        } else if (hasPinCode) {
            store.dispatch(requirePinCode());
        } else {
            // User has no PIN set, require full re-login
            store.dispatch(logout());
            window.location.href = '/login';
        }
    }
    
    return Promise.reject(error);
});

export default api;
